import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Code2,
  Share2,
  Layers,
  GitFork,
  Download,
  Cpu,
  Boxes,
  Workflow,
  Network,
  Database,
  Cloud,
  TerminalSquare,
} from "lucide-react";
import Header from "@/components/layout/Header";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.08 } },
};

const DIAGRAM_TYPES = [
  { icon: Workflow, name: "Flowcharts" },
  { icon: Layers, name: "Sequence Diagrams" },
  { icon: Boxes, name: "Class Diagrams" },
  { icon: Database, name: "ER Diagrams" },
  { icon: Cpu, name: "Gantt Charts" },
  { icon: Network, name: "Kanban Boards" },
  { icon: Cloud, name: "Quadrant Charts" },
  { icon: TerminalSquare, name: "Requirements" },
  { icon: Layers, name: "User Journeys" },
];

const FEATURES = [
  { icon: Code2, title: "DSL Engine", desc: "Write diagrams as code with our intuitive syntax inspired by Mermaid" },
  { icon: Sparkles, title: "AI Powered", desc: "Generate diagrams from plain English descriptions using GPT-5.2" },
  { icon: Share2, title: "Collaborate", desc: "Save, share, fork and version your diagrams with your team" },
  { icon: Download, title: "Export Anywhere", desc: "Export as SVG, PNG, or JSON. Embed directly in your docs" },
  { icon: Layers, title: "Multi-Format", desc: "14+ diagram types from flowcharts to Kubernetes architectures" },
  { icon: GitFork, title: "Fork & Iterate", desc: "Fork any public diagram and customize it for your needs" },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header transparent />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 via-white to-blue-50/30" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-20 pb-20 md:pt-28 md:pb-28">
          <motion.div {...stagger} initial="initial" animate="animate" className="max-w-3xl">
            <motion.p {...fadeUp} className="text-sm font-medium tracking-widest uppercase text-slate-400 mb-5 font-body">
              Next-Gen Diagram Platform
            </motion.p>
            <motion.h1
              {...fadeUp}
              className="text-5xl md:text-7xl font-bold tracking-tight text-slate-900 leading-[1.05] font-heading"
            >
              Text to Diagram.
              <br />
              Instantly.
            </motion.h1>
            <motion.p {...fadeUp} className="text-lg md:text-xl text-slate-500 mt-6 leading-relaxed max-w-xl font-body">
              Convert structured syntax, natural language, and AI prompts into beautiful, interactive architecture diagrams.
            </motion.p>
            <motion.div {...fadeUp} className="flex flex-wrap gap-3 mt-8">
              <Button
                size="lg"
                onClick={() => navigate("/editor")}
                data-testid="hero-start-btn"
                className="gap-2 h-11 px-6"
              >
                Start Creating <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/auth")}
                data-testid="hero-ai-btn"
                className="gap-2 h-11 px-6"
              >
                <Sparkles className="h-4 w-4" /> Try with AI
              </Button>
            </motion.div>
          </motion.div>

          {/* Editor Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-16 border border-slate-200 rounded-lg overflow-hidden shadow-2xl shadow-slate-200/50 bg-white"
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-rose-300" />
                <div className="w-3 h-3 rounded-full bg-amber-300" />
                <div className="w-3 h-3 rounded-full bg-emerald-300" />
              </div>
              <span className="text-xs text-slate-400 ml-2 font-mono">microservice-architecture.dsl</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
              <div className="p-6 bg-white">
                <pre
                  className="text-[13px] text-slate-600 leading-relaxed font-mono"
                  data-testid="hero-code-preview"
                >
{`graph TD
  Client[Client App] --> Gateway[API Gateway]
  Gateway --> Auth[Auth Service]
  Gateway --> Users[User Service]
  Gateway --> Orders[Order Service]
  Auth --> AuthDB[(Auth DB)]
  Users --> UserDB[(User DB)]
  Orders --> OrderDB[(Order DB)]`}
                </pre>
              </div>
              <div
                className="p-6 flex items-center justify-center bg-[#fafbfc] min-h-[280px]"
                style={{
                  backgroundImage: "radial-gradient(circle, #e2e8f0 1px, transparent 1px)",
                  backgroundSize: "16px 16px",
                }}
              >
                <HeroSVG />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Diagram Types */}
      <section className="py-14 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-sm font-medium tracking-widest uppercase text-slate-400 text-center mb-8 font-body">
            Supports 14+ Diagram Types
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {DIAGRAM_TYPES.map(({ icon: Icon, name }) => (
              <div
                key={name}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-full text-sm text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-all duration-200"
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 font-heading">
              Everything you need
            </h2>
            <p className="text-base md:text-lg text-slate-500 mt-4 max-w-lg mx-auto font-body">
              A complete platform for creating, sharing, and collaborating on architecture diagrams
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="border border-slate-200 rounded-lg p-6 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-slate-700" strokeWidth={1.5} />
                </div>
                <h3 className="font-medium text-slate-900 mb-1.5 font-heading">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-body">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-heading">
            Start building diagrams today
          </h2>
          <p className="text-base md:text-lg text-slate-400 mt-4 font-body">
            Free to use. No credit card required. Export unlimited diagrams.
          </p>
          <div className="flex justify-center gap-3 mt-8">
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate("/editor")}
              className="gap-2 h-11 px-6"
              data-testid="cta-start-btn"
            >
              Open Editor <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between text-sm text-slate-400 font-body">
          <span>© 2026 DiagramFoundry</span>
          <div className="flex items-center gap-6">
            <a href="/pricing" className="hover:text-slate-600">Pricing</a>
            <a href="/extensions" className="hover:text-slate-600">Extensions</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HeroSVG() {
  return (
    <svg viewBox="0 0 340 280" width="320" height="264" className="select-none">
      <defs>
        <marker id="hero-arr" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#64748b" />
        </marker>
      </defs>
      {/* Client */}
      <rect x="120" y="10" width="100" height="36" rx="4" fill="white" stroke="#0f172a" strokeWidth="1.5" />
      <text x="170" y="33" textAnchor="middle" fill="#0f172a" fontSize="12" fontFamily="Manrope, sans-serif" fontWeight="500">Client App</text>
      {/* Gateway */}
      <rect x="120" y="82" width="100" height="36" rx="4" fill="white" stroke="#0f172a" strokeWidth="1.5" />
      <text x="170" y="105" textAnchor="middle" fill="#0f172a" fontSize="12" fontFamily="Manrope, sans-serif" fontWeight="500">API Gateway</text>
      <line x1="170" y1="46" x2="170" y2="82" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#hero-arr)" />
      {/* Services */}
      <rect x="15" y="155" width="95" height="36" rx="4" fill="white" stroke="#0f172a" strokeWidth="1.5" />
      <text x="62" y="177" textAnchor="middle" fill="#0f172a" fontSize="11" fontFamily="Manrope, sans-serif" fontWeight="500">Auth Service</text>
      <rect x="123" y="155" width="95" height="36" rx="4" fill="white" stroke="#0f172a" strokeWidth="1.5" />
      <text x="170" y="177" textAnchor="middle" fill="#0f172a" fontSize="11" fontFamily="Manrope, sans-serif" fontWeight="500">User Service</text>
      <rect x="230" y="155" width="95" height="36" rx="4" fill="white" stroke="#0f172a" strokeWidth="1.5" />
      <text x="277" y="177" textAnchor="middle" fill="#0f172a" fontSize="11" fontFamily="Manrope, sans-serif" fontWeight="500">Order Service</text>
      <line x1="145" y1="118" x2="62" y2="155" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#hero-arr)" />
      <line x1="170" y1="118" x2="170" y2="155" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#hero-arr)" />
      <line x1="195" y1="118" x2="277" y2="155" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#hero-arr)" />
      {/* DBs */}
      <ellipse cx="62" cy="232" rx="32" ry="7" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <rect x="30" y="232" width="64" height="18" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <ellipse cx="62" cy="250" rx="32" ry="7" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <text x="62" y="246" textAnchor="middle" fill="#0f172a" fontSize="10" fontFamily="Manrope, sans-serif">Auth DB</text>
      <line x1="62" y1="191" x2="62" y2="225" stroke="#64748b" strokeWidth="1.2" markerEnd="url(#hero-arr)" />

      <ellipse cx="170" cy="232" rx="32" ry="7" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <rect x="138" y="232" width="64" height="18" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <ellipse cx="170" cy="250" rx="32" ry="7" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <text x="170" y="246" textAnchor="middle" fill="#0f172a" fontSize="10" fontFamily="Manrope, sans-serif">User DB</text>
      <line x1="170" y1="191" x2="170" y2="225" stroke="#64748b" strokeWidth="1.2" markerEnd="url(#hero-arr)" />

      <ellipse cx="277" cy="232" rx="32" ry="7" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <rect x="245" y="232" width="64" height="18" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <ellipse cx="277" cy="250" rx="32" ry="7" fill="white" stroke="#0f172a" strokeWidth="1.2" />
      <text x="277" y="246" textAnchor="middle" fill="#0f172a" fontSize="10" fontFamily="Manrope, sans-serif">Order DB</text>
      <line x1="277" y1="191" x2="277" y2="225" stroke="#64748b" strokeWidth="1.2" markerEnd="url(#hero-arr)" />
    </svg>
  );
}
